import { useEffect, useMemo, useState } from "react";
import { DataTable, Page, QuickStatsWidget } from "@iq/models";
import { listPaymentClients } from "../../services/paymentApi";

function formatDashboardDateTime(value) {
    if (!value || value === "-" || value === "N/A") {
        return "-";
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
        return String(value);
    }

    return parsed.toLocaleString();
}

function formatAmount(value) {
    if (value === null || value === undefined || value === "" || value === "N/A") {
        return "-";
    }

    const cents = Number(value);
    if (Number.isFinite(cents) && cents > 0) {
        return `$${cents.toLocaleString()}`;
    }

    return String(value);
}

const PAYMENT_DASHBOARD_TABLE_CONFIG = {
    columns: [
        {
            key: "stage",
            label: "Stage",
            accessor: "stage",
            filterable: true,
            quickValues: ["Active", "Pending", "Inactive"],
            width: ".75fr",
        },
        {
            key: "name",
            label: "Client",
            accessor: "name",
            filterable: true,
            quickValues: ["A–E", "F–J", "K–O", "P–T", "U–Z"],
            width: "1fr",
        },
        {
            key: "account",
            label: "Account",
            accessor: "account",
            filterable: true,
            quickValues: ["A–E", "F–J", "K–O", "P–T", "U–Z"],
            width: "2fr",
        },
        {
            key: "email",
            label: "Email",
            accessor: "email",
            filterable: false,
        },
        {
            key: "phone",
            label: "Phone",
            accessor: "phone",
            filterable: false,
        },
        {
            key: "plan",
            label: "Plan",
            accessor: "plan",
            filterable: false
        },
        {
            key: "paid",
            label: "Paid",
            accessor: "paid",
            filterable: false
        },
        {
            key: "nextPaymentDate",
            label: "Next Payment",
            accessor: (row) => formatDashboardDateTime(row?.nextPaymentDate),
            filterable: false
        },
        {
            key: "amount",
            label: "Amount",
            accessor: (row) => formatAmount(row?.amount),
            filterable: false,
        },
    ],
};

function Dashboard({ onClientSelect }) {
    const [clients, setClients] = useState([]);
    const [loadingClients, setLoadingClients] = useState(false);
    const [clientsError, setClientsError] = useState("");

    useEffect(() => {
        let isCancelled = false;

        async function loadClients() {
            setLoadingClients(true);
            setClientsError("");

            try {
                const data = await listPaymentClients();
                if (isCancelled) {
                    return;
                }
                setClients(Array.isArray(data) ? data : []);
            } catch (error) {
                if (isCancelled) {
                    return;
                }
                setClients([]);
                setClientsError(error?.message || "Failed to load clients");
            } finally {
                if (!isCancelled) {
                    setLoadingClients(false);
                }
            }
        }

        loadClients();

        return () => {
            isCancelled = true;
        };
    }, []);

    const tableRows = useMemo(
        () =>
            clients.map((client) => ({
                id: client.id,
                stage: client.status || "N/A",
                name: client.name || "N/A",
                account: client.company || "N/A",
                email: client.email || "N/A",
                phone: client.phone_number || "N/A",
                plan: client.plan || "N/A",
                paid: client.paid || "N/A",
                nextPaymentDate: client.next_payment_date || "N/A",
                amount: client.amount || "N/A",
            })),
        [clients]
    );

    return (
        <Page>
            <QuickStatsWidget
                variant="payment"
                stats={{
                    "Invoices Sent": 0,
                    "Quotes Sent": 0,
                    MRR: 0,
                    "Total Revenue": 0,
                }}
            />

            {clientsError && (
                <p style={{ color: "#ffb4b4", marginBottom: 12 }}>
                    {clientsError}
                </p>
            )}

            <DataTable
                rowData={tableRows}
                loading={loadingClients}
                tableConfig={PAYMENT_DASHBOARD_TABLE_CONFIG}
                onRowClick={(row) => onClientSelect?.(row)}
            />
        </Page>
    );
}

export default Dashboard;